import { useLazyQuery, useMutation } from '@apollo/client';
import { Alert, Empty, Form, Input, Spin, Table, Tag } from 'antd';
import { debounce, uniqBy } from 'lodash';
import { useCallback, useEffect, useRef, useState } from 'react';
import { LIMIT, SORT } from '../../common/constants';
import ModalComponent from '../ModalComponent';
import SelectComponent from '../SelectComponent';
import {
  getEnumConfig,
  TRANSFER_UNIT_MOVED_RESULT,
  TRANSFER_UNIT_RESULT,
} from './constants';
import { TRANSFER_INVENTORY_UNITS_ADMIN } from './graphql/Mutations';
import { TRANSFER_TARGET_LIBRARIES } from './graphql/Queries';

const { Item } = Form;
const LOADING_OPTION_KEY = '__loading__';
const { TextArea } = Input;

const RESULT_COLUMNS = [
  {
    title: 'CODE',
    dataIndex: 'code',
    key: 'code',
    render: (code) => code || '-',
  },
  {
    title: 'RESULT',
    dataIndex: 'result',
    key: 'result',
    render: (result) => {
      const config = getEnumConfig(TRANSFER_UNIT_RESULT, result);
      return (
        <>
          <Tag color={config?.color}>{config?.label}</Tag>
          {config?.hint && <div className="font-size-12">{config?.hint}</div>}
        </>
      );
    },
  },
];

const TransferUnitsModal = ({ open, onClose, units = [], onTransferred }) => {
  const [form] = Form.useForm();
  const [results, setResults] = useState(null);
  const [error, setError] = useState('');

  const [libraryList, setLibraryList] = useState([]);
  const [isAllLibrariesFetched, setIsAllLibrariesFetched] = useState(false);
  const [isFetchingMoreLibraries, setIsFetchingMoreLibraries] = useState(false);
  const librarySearchRef = useRef('');
  const librarySkipRef = useRef(0);
  const libraryListRef = useRef(null);

  const [getLibraries, { loading: isLoadingLibraries }] = useLazyQuery(
    TRANSFER_TARGET_LIBRARIES,
    {
      fetchPolicy: 'network-only',
      onCompleted: (response) => {
        const rows = response?.libraries?.data || [];
        if (rows?.length < LIMIT) setIsAllLibrariesFetched(true);
        setLibraryList((prev) => uniqBy([...prev, ...rows], 'id'));
        setIsFetchingMoreLibraries(false);
      },
      onError() {
        setIsFetchingMoreLibraries(false);
      },
    },
  );
  const [transferUnits, { loading: isTransferring }] = useMutation(
    TRANSFER_INVENTORY_UNITS_ADMIN,
    { errorPolicy: 'all', onError() {} },
  );

  const loadLibraries = (search, skip) => {
    librarySearchRef.current = search;
    librarySkipRef.current = skip;
    getLibraries({
      variables: {
        filter: { limit: LIMIT, skip, search: search || undefined },
        sort: { field: 'name', order: SORT?.ASC },
      },
    });
  };

  useEffect(() => {
    if (!open) return;
    setLibraryList([]);
    setIsAllLibrariesFetched(false);
    setIsFetchingMoreLibraries(false);
    loadLibraries('', 0);
  }, [open]);

  const handleLibrarySearch = useCallback(
    debounce((value) => {
      setLibraryList([]);
      setIsAllLibrariesFetched(false);
      setIsFetchingMoreLibraries(false);
      loadLibraries(value?.trim(), 0);
    }, 500),
    [],
  );

  const handleLibraryScroll = (event) => {
    const target = event?.target;
    libraryListRef.current = target;
    if (
      isFetchingMoreLibraries ||
      isAllLibrariesFetched ||
      isLoadingLibraries ||
      target?.scrollTop + target?.offsetHeight < target?.scrollHeight - 20
    ) {
      return;
    }
    setIsFetchingMoreLibraries(true);
    loadLibraries(librarySearchRef?.current, librarySkipRef?.current + LIMIT);
  };

  useEffect(() => () => handleLibrarySearch?.cancel(), [handleLibrarySearch]);

  useEffect(() => {
    if (!isFetchingMoreLibraries || !libraryListRef?.current) return;
    libraryListRef.current.scrollTop = libraryListRef?.current?.scrollHeight;
  }, [isFetchingMoreLibraries]);

  const codes = units
    ?.map((unit) => unit?.unitCode || unit?.qrCode)
    ?.filter(Boolean);

  const handleClose = () => {
    form?.resetFields();
    setResults(null);
    setError('');
    onClose();
  };

  const handleFinish = async (values) => {
    setError('');
    const response = await transferUnits({
      variables: {
        data: {
          codes,
          toLibraryId: values?.toLibraryId,
          reason: values?.reason?.trim() || undefined,
        },
      },
    });
    const data = response?.data?.transferInventoryUnitsAdmin?.data;
    if (!data) {
      setError(
        response?.errors?.[0]?.message ||
          'Could not transfer these units. Please try again.',
      );
      return;
    }
    setResults({
      movedCount: data?.movedCount || 0,
      rows: data?.results || [],
      message: response?.data?.transferInventoryUnitsAdmin?.message,
    });
    if (data?.movedCount > 0 && onTransferred) onTransferred(data);
  };

  const failedCount = results ? codes?.length - results?.movedCount : 0;

  return (
    <ModalComponent
      title="Transfer units"
      okText="Transfer"
      cancelText={results ? 'Close' : 'Cancel'}
      open={open}
      onOk={form?.submit}
      onCancel={handleClose}
      confirmLoading={isTransferring}
      okButtonProps={{ style: results ? { display: 'none' } : undefined }}
      className="modal transfer-units-modal"
      destroyOnClose
    >
      {results ? (
        <>
          <Alert
            className="mb-16"
            type={failedCount > 0 ? 'warning' : 'success'}
            showIcon
            message={
              results?.message ||
              `${results?.movedCount} of ${codes?.length} units moved.`
            }
            description={
              failedCount > 0
                ? `${failedCount} could not be moved. See why below.`
                : undefined
            }
          />
          <Table
            size="small"
            rowKey={(record) => record?.code}
            columns={RESULT_COLUMNS}
            dataSource={results?.rows}
            pagination={false}
            rowClassName={(record) =>
              record?.result === TRANSFER_UNIT_MOVED_RESULT
                ? ''
                : 'transfer-units-failed-row'
            }
          />
        </>
      ) : (
        <>
          <Alert
            className="mb-16"
            type="info"
            showIcon
            message={`${codes?.length} ${
              codes?.length === 1 ? 'unit' : 'units'
            } will move to the library you pick. Their QR and unit codes stay the same.`}
          />
          <Form form={form} layout="vertical" onFinish={handleFinish}>
            <Item
              name="toLibraryId"
              label="Move to library"
              rules={[{ required: true, message: 'Please Select a Library!' }]}
            >
              <SelectComponent
                placeholder="Search library"
                loading={isLoadingLibraries && !isFetchingMoreLibraries}
                filterOption={false}
                onSearch={handleLibrarySearch}
                onPopupScroll={handleLibraryScroll}
                notFoundContent={
                  isLoadingLibraries ? (
                    <Spin size="small" />
                  ) : (
                    <Empty image={Empty?.PRESENTED_IMAGE_SIMPLE} />
                  )
                }
              >
                {libraryList?.map((library) => (
                  <SelectComponent.Option key={library?.id} value={library?.id}>
                    {library?.libraryNumber
                      ? `${library?.name} (${library?.libraryNumber})`
                      : library?.name}
                  </SelectComponent.Option>
                ))}
                {isFetchingMoreLibraries && (
                  <SelectComponent.Option
                    key={LOADING_OPTION_KEY}
                    value={LOADING_OPTION_KEY}
                    disabled
                  >
                    <div className="select-load-more">
                      <Spin size="small" />
                    </div>
                  </SelectComponent.Option>
                )}
              </SelectComponent>
            </Item>
            <Item name="reason" label="Reason">
              <TextArea
                rows={3}
                allowClear
                showCount
                maxLength={300}
                placeholder="Why are these units moving? Saved in the unit history."
              />
            </Item>
          </Form>
          {error && <Alert type="error" showIcon message={error} />}
        </>
      )}
    </ModalComponent>
  );
};

export default TransferUnitsModal;
